import type { Theme } from './types';

/**
 * Emits the theme as CSS custom properties under `:root`, using the same names
 * the Nocturne stylesheet uses. Web components can then keep writing
 * `var(--color-accent)` exactly as the prototype did, and a CMS theme swap is a
 * single style-tag rewrite with no re-render of the tree.
 */
export function themeToCssVars(theme: Theme): string {
  const c = theme.colors;
  const lines: string[] = [
    `--color-bg: ${c.bg}`,
    `--color-surface: ${c.surface}`,
    `--color-text: ${c.text}`,
    `--color-accent: ${c.accent}`,
    `--color-accent-2: ${c.accent2}`,
    `--color-divider: ${c.divider}`,
    `--color-section: ${c.section}`,
    `--color-section-glow: ${c.sectionGlow}`,
    `--color-section-ghost: ${c.sectionGhost}`,
    `--font-heading: "${theme.type.heading}", system-ui, sans-serif`,
    `--font-body: "${theme.type.body}", system-ui, sans-serif`,
    `--font-mono: ${theme.type.mono}`,
    `--font-heading-weight: ${theme.type.headingWeight}`,
    `--space-1: ${theme.space.s1}px`,
    `--space-2: ${theme.space.s2}px`,
    `--space-3: ${theme.space.s3}px`,
    `--space-4: ${theme.space.s4}px`,
    `--space-6: ${theme.space.s6}px`,
    `--space-8: ${theme.space.s8}px`,
    `--radius-sm: ${theme.radius.sm}px`,
    `--radius-md: ${theme.radius.md}px`,
    `--radius-lg: ${theme.radius.lg}px`,
    `--shadow-sm: ${theme.shadow.sm}`,
    `--shadow-md: ${theme.shadow.md}`,
    `--shadow-lg: ${theme.shadow.lg}`,
    `--rule-fade: ${theme.ruleFade}px`,
    // Bare "r, g, b" triplets. Components build alpha blends as
    // rgba(var(--color-text-rgb), .55), so every translucent value in the app
    // follows the active theme instead of a baked-in Nocturne literal.
    `--color-text-rgb: ${rgbTriplet(c.text)}`,
    `--color-accent-rgb: ${rgbTriplet(c.accent)}`,
    `--color-bg-rgb: ${rgbTriplet(c.bg)}`,
  ];

  for (const [step, hex] of Object.entries(c.neutral)) lines.push(`--color-neutral-${step}: ${hex}`);
  for (const [step, hex] of Object.entries(c.accentRamp)) lines.push(`--color-accent-${step}: ${hex}`);
  for (const [step, hex] of Object.entries(c.accent2Ramp)) lines.push(`--color-accent-2-${step}: ${hex}`);
  for (const [track, hex] of Object.entries(c.tracks)) {
    lines.push(`--track-${track.toLowerCase()}: ${hex}`);
  }

  return `:root {\n  ${lines.join(';\n  ')};\n}`;
}

/** "r, g, b" from a hex, for building rgba() at call sites. */
export function rgbTriplet(hex: string): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((x) => x + x).join('') : h;
  const n = parseInt(full, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

/** rgba() from a hex + alpha — RN has no color-mix, and inline rgba is used throughout. */
export function alpha(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((x) => x + x).join('') : h;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}
